// src/pages/Auth/Register.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box, Link,
  Alert, CircularProgress, FormControl, InputLabel, Select,
  MenuItem, Grid, Divider, InputAdornment, IconButton,
} from '@mui/material';
import { PersonAdd, Google, Store, ShoppingCart, Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const Register = () => {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') === 'seller' ? 'seller' : 'buyer';

  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '',
    company_name: '', phone: '', country: '', user_type: initialType,
  });
  const [errors,        setErrors]        = useState({});
  const [loading,       setLoading]       = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword,  setShowPassword]  = useState(false);
  const [showConfirm,   setShowConfirm]   = useState(false);

  const navigate     = useNavigate();
  const { refreshUser } = useAuth();
  const intervalRef  = useRef(null);

  const countries = [
    'India','UAE','USA','UK','Canada','Australia','Germany','France',
    'Japan','China','Singapore','Malaysia','Thailand','Vietnam','South Africa',
    'Brazil','Chile','Peru','Mexico','Spain','Italy','Netherlands','Belgium',
    'Saudi Arabia','Qatar','Oman','Kuwait','Bahrain','Sri Lanka','Bangladesh',
    'Pakistan','Nepal','Myanmar','Indonesia','Philippines','South Korea','Taiwan',
    'New Zealand','Russia','Turkey','Egypt','Kenya','Nigeria','Ghana','Morocco',
  ];

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // ── Redirect helper ─────────────────────────────────────────────────────────
  const redirectByRole = (userType) => {
    if      (userType === 'admin')  navigate('/admin/dashboard');
    else if (userType === 'seller') navigate('/seller/dashboard');
    else if (userType === 'buyer')  navigate('/buyer/dashboard');
    else                            navigate('/auth/select-role');
  };

  // ── Field change ────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateForm = () => {
    const e = {};
    if (!formData.email)                          e.email           = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email           = 'Email is invalid';
    if (!formData.password)                       e.password        = 'Password is required';
    else if (formData.password.length < 6)        e.password        = 'Min 6 characters';
    if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!formData.company_name)                   e.company_name    = 'Company name is required';
    if (!formData.phone)                          e.phone           = 'Phone number is required';
    else if (!/^[+]?[\d\s\-()]{8,}$/.test(formData.phone)) e.phone = 'Enter a valid phone number';
    if (!formData.country)                        e.country         = 'Country is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Email register ──────────────────────────────────────────────────────────
  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      setErrors({});
      const response = await authAPI.register({
        email: formData.email, password: formData.password,
        company_name: formData.company_name, phone: formData.phone,
        country: formData.country, user_type: formData.user_type,
      });
      if (response.data.success) {
        const { user, access_token } = response.data;
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
        await refreshUser();
        toast.success('Registration successful! Welcome to Fruit Export Platform.');
        setTimeout(() => redirectByRole(user.user_type), 800);
      } else {
        setErrors({ submit: response.data.message || 'Registration failed' });
        toast.error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Registration failed. Please try again.';
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Google register ─────────────────────────────────────────────────────────
  const handleGoogleSignUp = async () => {
    try {
      setGoogleLoading(true);
      setErrors({});

      const response = await authAPI.googleLogin();
      if (!response.data.success || !response.data.auth_url) {
        throw new Error(response.data.message || 'Failed to get Google sign-up URL');
      }

      const width = 600, height = 700;
      const left  = window.screenX + (window.outerWidth  - width)  / 2;
      const top   = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        response.data.auth_url,
        'Google Sign Up',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        setErrors({ submit: 'Popup blocked! Please allow popups for this site.' });
        toast.error('Popup blocked!');
        setGoogleLoading(false);
        return;
      }

      // ✅ Listen for postMessage sent by GoogleCallback in the popup
      const handleMessage = async (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'GOOGLE_AUTH_REDIRECT') return;

        window.removeEventListener('message', handleMessage);
        if (intervalRef.current) clearInterval(intervalRef.current);

        const { redirectUrl } = event.data;

        // Don't call refreshUser here — GoogleCallback already saved token+user
        // with correct user_type (null for new users). refreshUser would call /me
        // and might overwrite with wrong data in edge cases.
        setGoogleLoading(false);

        // Navigate via React Router
        const url = new URL(redirectUrl, window.location.origin);
        navigate(url.pathname + url.search);
      };

      window.addEventListener('message', handleMessage);

      // ✅ Fallback: popup closed without postMessage
      intervalRef.current = setInterval(async () => {
        if (popup.closed) {
          clearInterval(intervalRef.current);
          window.removeEventListener('message', handleMessage);

          const storedUser  = localStorage.getItem('user');
          const storedToken = localStorage.getItem('token');

          if (storedUser && storedToken) {
            const user = JSON.parse(storedUser);
            toast.success(`Welcome, ${user.company_name || user.email}!`);
            // If no role yet → go to select-role
            if (!user.user_type) {
              navigate('/auth/select-role');
            } else {
              redirectByRole(user.user_type);
            }
          } else {
            console.log('Popup closed without completing auth');
          }
          setGoogleLoading(false);
        }
      }, 500);

    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Google sign-up failed.';
      setErrors({ submit: msg });
      toast.error(msg);
      setGoogleLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <PersonAdd sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>Create Account</Typography>
            <Typography variant="body1" color="text.secondary">
              Join the global fruit trading platform
            </Typography>
          </Box>

          {errors.submit && <Alert severity="error" sx={{ mb: 3 }}>{errors.submit}</Alert>}

          {/* Google Sign Up */}
          <Button fullWidth variant="outlined" size="large"
            startIcon={googleLoading ? <CircularProgress size={20} /> : <Google />}
            onClick={handleGoogleSignUp} disabled={googleLoading || loading}
            sx={{ mb: 3, py: 1.5, backgroundColor: '#fff', borderColor: '#ddd',
                  color: '#444', '&:hover': { backgroundColor: '#f8f8f8', borderColor: '#ccc' } }}>
            {googleLoading ? 'Opening Google...' : 'Sign up with Google'}
          </Button>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">Or register with email</Typography>
          </Divider>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Account type */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>I want to:</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Button fullWidth
                      variant={formData.user_type === 'buyer' ? 'contained' : 'outlined'}
                      startIcon={<ShoppingCart />}
                      onClick={() => setFormData(prev => ({ ...prev, user_type: 'buyer' }))}
                      sx={{ py: 2.5, borderRadius: 2 }}>
                      Buy Fruits
                    </Button>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      Import fruits from suppliers
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button fullWidth
                      variant={formData.user_type === 'seller' ? 'contained' : 'outlined'}
                      startIcon={<Store />}
                      onClick={() => setFormData(prev => ({ ...prev, user_type: 'seller' }))}
                      sx={{ py: 2.5, borderRadius: 2 }}>
                      Sell Fruits
                    </Button>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      Export fruits to buyers
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Email Address" name="email" type="email"
                  value={formData.email} onChange={handleChange}
                  error={!!errors.email} helperText={errors.email}
                  disabled={loading || googleLoading} autoComplete="email" />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Company Name" name="company_name"
                  value={formData.company_name} onChange={handleChange}
                  error={!!errors.company_name} helperText={errors.company_name}
                  disabled={loading || googleLoading} autoComplete="organization" />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Phone Number" name="phone"
                  value={formData.phone} onChange={handleChange}
                  error={!!errors.phone} helperText={errors.phone}
                  disabled={loading || googleLoading} placeholder="+1234567890" autoComplete="tel" />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.country}>
                  <InputLabel>Country</InputLabel>
                  <Select name="country" value={formData.country} label="Country"
                    onChange={handleChange} disabled={loading || googleLoading}>
                    <MenuItem value="">Select Country</MenuItem>
                    {countries.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                  {errors.country && (
                    <Typography variant="caption" color="error">{errors.country}</Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Password" name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password} onChange={handleChange}
                  error={!!errors.password} helperText={errors.password}
                  disabled={loading || googleLoading} autoComplete="new-password"
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(p => !p)} edge="end" size="small">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )}} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Confirm Password" name="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={formData.confirmPassword} onChange={handleChange}
                  error={!!errors.confirmPassword} helperText={errors.confirmPassword}
                  disabled={loading || googleLoading} autoComplete="new-password"
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm(p => !p)} edge="end" size="small">
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )}} />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
              <Typography variant="body2">
                {formData.user_type === 'buyer' ? (
                  <><strong>Buyer Account:</strong> Browse and import fruits · Track shipments · Manage orders</>
                ) : (
                  <><strong>Seller Account:</strong> List fruits for export · Manage enquiries · Track inventory</>
                )}
                <br /><br />
                <strong>All Accounts:</strong> Business verification required · Secure encrypted transactions
              </Typography>
            </Alert>

            <Button type="submit" fullWidth variant="contained" size="large"
              disabled={loading || googleLoading}
              startIcon={loading ? <CircularProgress size={20} /> : <PersonAdd />}
              sx={{ mb: 3, py: 1.5 }}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>

            <Box sx={{ textAlign: 'center', pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link component={RouterLink} to="/login" underline="hover"
                  fontWeight="medium" color="primary">Sign in here</Link>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                By creating an account, you agree to our{' '}
                <Link href="#" underline="hover" color="primary">Terms of Service</Link>{' '}
                and{' '}
                <Link href="#" underline="hover" color="primary">Privacy Policy</Link>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;